import { apiFetch } from "./client";

export type InvoiceStatus = "OPEN" | "PARTIALLY_PAID" | "PAID" | "CANCELLED";

/** `kind` tells the screen whether a combined view exists for this company. */
export type CompanyRef = { id: string; name: string; country: string | null; kind: string | null };

/**
 * What the company owes across all its accounts, in euro at the latest rate: `currency` is always
 * EUR. `byCurrency` is the same balance as each account holds it, and `rateDate` the day of the
 * rate the euro figures stand on (null when everything is in euro).
 */
export type AccountView = {
  currency: string;
  balanceTotal: number;
  overdueTotal: number;
  creditLimit: number | null;
  creditAvailable: number | null;
  byCurrency: AccountCurrency[];
  rateDate: string | null;
};

/** One account in its own currency, as the ledger holds it. */
export type AccountCurrency = { currency: string; balance: number; overdue: number };

/**
 * What the customer paid ahead, in each currency as the ERP's receivables snapshot holds it, and the
 * day of that snapshot. It stands beside the balance and is not taken from it. Internal only: the
 * API leaves it out of the customer's own view, and sends it only when there is an amount to show.
 */
export type AdvanceView = {
  snapshotDate: string;
  byCurrency: { currency: string; amount: number }[];
};

/**
 * Otvoreno po kašnjenju; pragovi su isti kao na spisku potraživanja (`AgingBuckets` na serveru).
 * Dva ekrana jedan klik daleko ne smeju da seku isti dug različito.
 */
export type AgingView = {
  /**
   * Valuta ovih kofa — razlaganje ide PO VALUTI i ne preračunava se.
   *
   * Dok je bila jedna traka, firma sa 711,08 EUR i 40.321,08 USD dobijala je kofu od 41.032,16
   * označenu dolarom, jer je valuta uzeta sa „glavnog" naloga. Ista strana je tada sama sebe
   * obarala: zbir kofa 41.033,78 prema KPI saldu 40.321,08.
   */
  currency: string;
  notDue: number;
  d7: number;
  d30: number;
  d60: number;
  d120: number;
  d120Plus: number;
  /** Prenos iz ranijih godina: dospelo, starost nepoznata. */
  carried: number;
};

export type InvoiceView = {
  id: string;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  currency: string;
  grossAmount: number;
  paidAmount: number;
  openAmount: number;
  status: InvoiceStatus;
  overdue: boolean;
  orderReference: string | null;
  /**
   * Predmet dokumenta iz ERP-a („Zakup Integra IX/26").
   *
   * Kod računa za usluge je to jedini opis onoga što je naplaćeno, jer takav račun nema stavku
   * ni u izvoru; kod računa za robu stoji kao naslov iznad robe.
   */
  description: string | null;
  /** Vrsta dokumenta iz ERP-a, doslovno; ne svodi se na sopstveni šifarnik. */
  documentType: string | null;
  /** Bankom je račun označio kao sporan; samo za zaposlene i samo za račune kupaca (dobavljač ga ne nosi). */
  disputed?: boolean;
  /** Zašto je sporan; prazno kad nije. */
  disputeReason?: string | null;
};

export type PaymentView = {
  id: string;
  paymentDate: string;
  currency: string;
  amount: number;
  method: string | null;
  reference: string | null;
  invoiceNumber: string | null;
};

export type TurnoverBin = { label: string; from: string; to: string; invoicedEur: number };

export type FinanceDetail = {
  company: CompanyRef;
  account: AccountView;
  /** The advance received; only on the internal card of a customer, and only when there is one. */
  advanceReceived?: AdvanceView | null;
  /** Starosno razlaganje PO VALUTI — jedan red po valuti u kojoj firma ima otvoreno. */
  aging: AgingView[];
  invoices: InvoiceView[];
  payments: PaymentView[];
  turnover: { periods: TurnoverBin[] };
  from: string;
  to: string;
  /**
   * The day overdue and the aging are counted on: the day the outgoing invoices reach, or today if
   * that comes first. The screen names it beside both.
   */
  asOf?: string | null;
};

export type CompanyRow = {
  companyId: string;
  name: string;
  country: string | null;
  /** Saldo preračunat u evre. */
  balanceEur: number;
  overdueEur: number;
  /** Otvoreno sa rokom u budućnosti. */
  notDueEur: number;
  /** Kašnjenje 1–7, 8–30, 31–60, 61–120 i preko 120 dana. */
  d7Eur: number;
  d30Eur: number;
  d60Eur: number;
  d120Eur: number;
  d120PlusEur: number;
  /** Prenos iz ranijih godina: dospelo, starost nepoznata. */
  carriedEur: number;
  /**
   * Saldo po valuti, kako stoji na računima. Kupac može istovremeno dugovati u dinarima i u
   * evrima, a ta dva se ne sabiraju bez kursa, pa vidljivi broj ostaje preračunat a razlaganje
   * stoji iza dugmeta.
   */
  balanceByCurrency: Record<string, number>;
  creditLimit: number | null;
  turnoverEur: number;
  lastPaymentDate: string | null;
};

/** Otvoreno u jednoj valuti, onako kako stoji na računima. */
export type CurrencyTotal = {
  currency: string;
  balance: number;
  overdue: number;
  notDue: number;
};

export type FinanceOverview = {
  totals: {
    receivablesEur: number;
    overdueEur: number;
    notDueEur: number;
    d7Eur: number;
    d30Eur: number;
    d60Eur: number;
    d120Eur: number;
    d120PlusEur: number;
  /** Prenos iz ranijih godina: dospelo, starost nepoznata. */
  carriedEur: number;
    /** Prosečna naplata u danima; prazno kad u periodu nije bilo fakturisanja. */
    dso: number | null;
    /** Koliki deo duga drži pet najvećih dužnika, u procentima. */
    top5Pct: number | null;
    /**
     * Isti zbir razložen po valutama, bez preračuna, od najvećeg.
     *
     * Prazno kad je valuta izabrana: tada su svi iznosi na ekranu ionako u toj jednoj.
     */
    byCurrency: CurrencyTotal[];
    /** Invoiced in the period, VAT included: invoices only, without the opening balance. */
    turnoverEur: number;
    /** The opening balance posted in the period, left out of `turnoverEur`. */
    carriedPostedEur: number;
    /** Other debits posted in the period: reposts, rate differences, ledger orders. */
    otherPostedEur: number;
    companyCount: number;
    companiesWithOverdue: number;
    /** How many amounts were left out of the euro totals for want of a rate. */
    unconvertedCount: number;
  };
  filters: {
    marketId: string | null; country: string | null; currency: string | null;
    from: string; to: string;
  };
  /** Države koje se pojavljuju među kupcima u opsegu; puni padajući filter. */
  availableCountries: string[];
  /** Valute u kojima kupci u opsegu stvarno duguju; puni padajući filter. */
  availableCurrencies: string[];
  companies: CompanyRow[];
  page: number;
  size: number;
  totalElements: number;
  /** The day overdue and the aging are counted on, as on the card of a customer. */
  asOf?: string | null;
};

/**
 * Upitni deo adrese. Broj se propušta kao broj, i nula se šalje: `page=0` je prva strana, a
 * provera istinitosti bi je ispustila i vratila korisnika na podrazumevanu.
 */
const q = (params: Record<string, string | number | boolean | undefined>) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    usp.set(k, String(v));
  });
  const s = usp.toString();
  return s ? `?${s}` : "";
};

export type Period = { from?: string; to?: string };

/** Grupe kašnjenja na ekranu finansija; pragovi dogovoreni sa korisnikom. */
/**
 * Grupe kašnjenja, i jedna koja to nije.
 *
 * `PRENOS` je saldo prenet iz ranijih godina: ERP svake godine zaključi knjigu i otvori narednu
 * jednim knjiženjem po kupcu, datiranim na 1. januar. Iznos JE dospeo i ostaje u svakom zbiru, ali
 * 1. januar je dan prenosa a ne dan nastanka duga — pa u kofi kašnjenja tvrdi starost koju niko ne
 * zna. Zato stoji zasebno, i zato je jedina koja se ne izvodi iz datuma nego iz zapisa.
 */
export const BUCKETS = ["NEDOSPELO", "D7", "D30", "D60", "D120", "D120P", "PRENOS"] as const;
export type Bucket = (typeof BUCKETS)[number];

/** Sledljivost jednog računa: odakle je roba otišla i pod kojim je LOT oznakama. */
export type InvoiceTraceability = {
  dispatches: DispatchRef[];
  lots: LotRef[];
  /** The invoice has no lines with an article, so the lots of its whole dispatch notes are listed. */
  wholeNotes: boolean;
};

export type DispatchRef = {
  /** Oznaka iz ERP-a; stoji i kad se ne razreši u dokument. */
  reference: string;
  noteId: string | null;
  number: string | null;
  issueDate: string | null;
  /** The dispatch order the note was issued on; null when the note names none. */
  dispatchOrderNumber: string | null;
  /** What the dispatch order was made from; all three are null when it has no source. */
  sourceKind: "OFFER" | "ORDER" | null;
  sourceId: string | null;
  sourceNumber: string | null;
};

export type LotRef = {
  lotId: string;
  designation: string | null;
  productName: string | null;
  producedOn: string | null;
  expiresOn: string | null;
  country: string | null;
  quantity: number | null;
  unit: string | null;
};

export const financeApi = {
  me: (p: Period = {}) => apiFetch<FinanceDetail>(`/finance/me${q(p)}`),
  overview: (
    p: {
      marketId?: string; country?: string; currency?: string; from?: string; to?: string;
      /** Grupa kašnjenja: NEDOSPELO, D7, D30, D60, D120, D120P — ili PRENOS, koji nije kašnjenje. */
      bucket?: Bucket; q?: string;
      /** Redosled spiska; vidi REDOSLEDI na ekranu finansija. */
      sort?: string;
      page?: number; size?: number;
      /** Sa spornim potraživanjima; bez toga sporni računi ne ulaze ni u zbirove ni u spisak. */
      sporna?: boolean;
    } = {},
  ) =>
    apiFetch<FinanceOverview>(`/finance/overview${q(p)}`),
  traceability: (invoiceId: string) =>
    apiFetch<InvoiceTraceability>(`/finance/invoices/${invoiceId}/traceability`),
  company: (id: string, p: Period & { sporna?: boolean } = {}) =>
    apiFetch<FinanceDetail>(`/finance/companies/${id}${q(p)}`),
};
